/**
 * @-mention markup (ADR-001 D4). Rich text stores a mention as
 * `<span data-type="mention" data-object-type data-id data-label>@Label</span>`.
 *
 * Dependency-free and DOM-free, so the SPA and the MCP Worker extract the same
 * targets from the same HTML. It is a single linear pass that reads tags the
 * way the HTML parser does. Attributes are read in order and the first of a
 * name wins. Quoted values, comments and raw-text elements are skipped, so a
 * mention written inside them is not a mention. An unterminated tag ends the
 * document.
 */
import { isWorkspaceObjectType, type WorkspaceObjectType } from "./workspace-object-types";

export interface MentionTarget {
  type: WorkspaceObjectType;
  id: string;
  /** The label written into the markup, for messages; never trusted as identity. */
  label?: string;
}

/** relay.sync_mention_links refuses more targets than this. */
export const MAX_MENTION_TARGETS = 200;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " " };
const RAW_TEXT = new Set(["script", "style", "textarea", "title", "xmp", "noscript", "noembed", "noframes", "iframe"]);
// HTML separates tokens only on ASCII whitespace; JS's \s also matches NBSP and
// other Unicode spaces, which the HTML parser keeps inside names and values.
const SPACE = /[\t\n\f\r ]/;

// Numeric references decode as the HTML parser decodes them (";" optional; NUL,
// surrogates and out-of-range code points become U+FFFD), plus the named ones a
// serializer writes.
const fromCodePoint = (code: number) =>
  code === 0 || code > 0x10ffff || (code >= 0xd800 && code <= 0xdfff) ? "\ufffd" : String.fromCodePoint(code);
const decode = (value: string) =>
  value.replace(/&(?:#x([0-9a-f]+);?|#(\d+);?|(amp|lt|gt|quot|apos|nbsp);)/gi, (whole, hex?: string, dec?: string, named?: string) =>
    hex !== undefined ? fromCodePoint(parseInt(hex, 16))
      : dec !== undefined ? fromCodePoint(Number(dec))
        : ENTITIES[named?.toLowerCase() ?? ""] ?? whole);

/** Reads attributes from just past a tag name to its `>`; null if the document ends first. */
function readTag(html: string, from: number): { attributes: Map<string, string>; end: number } | null {
  const attributes = new Map<string, string>();
  let i = from;
  while (i < html.length) {
    const c = html.charAt(i);
    if (c === ">") return { attributes, end: i + 1 };
    if (SPACE.test(c) || c === "/") {
      i++;
      continue;
    }
    let j = i + 1;
    while (j < html.length && !SPACE.test(html.charAt(j)) && !"/>=".includes(html.charAt(j))) j++;
    const name = html.slice(i, j).toLowerCase();
    i = j;
    while (i < html.length && SPACE.test(html.charAt(i))) i++;
    let value = "";
    if (html.charAt(i) === "=") {
      i++;
      while (i < html.length && SPACE.test(html.charAt(i))) i++;
      const quote = html.charAt(i);
      if (quote === '"' || quote === "'") {
        const close = html.indexOf(quote, i + 1);
        if (close < 0) return null;
        value = html.slice(i + 1, close);
        i = close + 1;
      } else {
        let k = i;
        while (k < html.length && !SPACE.test(html.charAt(k)) && html.charAt(k) !== ">") k++;
        value = html.slice(i, k);
        i = k;
      }
    }
    if (!attributes.has(name)) attributes.set(name, decode(value));
  }
  return null;
}

/** Distinct, well-formed mention targets in document order. */
export function extractHtmlMentions(html: string): MentionTarget[] {
  const targets = new Map<string, MentionTarget>();
  let i = 0;
  while ((i = html.indexOf("<", i)) !== -1) {
    if (html.startsWith("<!--", i)) {
      // "<!-->" and "<!--->" are complete (empty) comments to the HTML parser.
      if (html.startsWith("<!-->", i) || html.startsWith("<!--->", i)) {
        i = html.indexOf(">", i) + 1;
        continue;
      }
      // A comment ends at "-->" or, as browsers also accept, "--!>": whichever comes first.
      const end = /--!?>/g;
      end.lastIndex = i + 4;
      const close = end.exec(html);
      if (!close) break;
      i = end.lastIndex;
      continue;
    }
    // Any other "<!", "<?" or "</" not followed by a letter is a bogus comment up
    // to the next ">"; "</name" is an end tag, read through its attributes, so
    // markup quoted inside either is never taken for a tag.
    const next = html.charAt(i + 1);
    if (next === "!" || next === "?" || (next === "/" && !/[a-z]/i.test(html.charAt(i + 2)))) {
      const close = html.indexOf(">", i + 2);
      if (close < 0) break;
      i = close + 1;
      continue;
    }
    if (next === "/") {
      const endName = /^<\/([a-z][^\t\n\f\r />]*)/i.exec(html.slice(i, i + 64))?.[1] ?? "";
      const endTag = readTag(html, i + 2 + endName.length);
      if (!endTag) break;
      i = endTag.end;
      continue;
    }
    const name = /^<([a-z][^\t\n\f\r />]*)/i.exec(html.slice(i, i + 64))?.[1].toLowerCase();
    if (!name) {
      i++;
      continue;
    }
    const tag = readTag(html, i + 1 + name.length);
    if (!tag) break;
    i = tag.end;
    // <plaintext> makes the rest of the document text; nothing (not even
    // "</plaintext>") ends it.
    if (name === "plaintext") break;
    if (RAW_TEXT.has(name)) {
      // Case-insensitive in the original string: lowercasing can change its length
      // (e.g. "İ"), which would shift every offset after it. As in the HTML parser,
      // only a full tag name ends it: </textareax> is still text.
      const close = new RegExp(`</${name}(?=[\\t\\n\\f\\r />])`, "gi");
      close.lastIndex = i;
      const found = close.exec(html);
      if (!found) break;
      i = found.index;
      continue;
    }
    if (name !== "span" || tag.attributes.get("data-type") !== "mention") continue;
    const type = tag.attributes.get("data-object-type");
    const id = tag.attributes.get("data-id")?.toLowerCase();
    if (!type || !isWorkspaceObjectType(type) || !id || !UUID.test(id)) continue;
    const key = `${type}:${id}`;
    if (targets.has(key)) continue;
    // DOMPurify drops a data-label containing "-->" and the like; the chip's text still has it.
    const text = html.indexOf("<", i);
    const label = tag.attributes.get("data-label")
      || decode(html.slice(i, text < 0 ? html.length : text)).trim().replace(/^@/, "");
    targets.set(key, label ? { type, id, label } : { type, id });
  }
  return [...targets.values()];
}

// Notes are plain text (relay.activities.body), so a mention is written inline
// as `@[Label](relay://type/id)` (ADR-001 open question 1). A "[", "]" or "\" in
// the label is escaped with "\"; a line break becomes a space. An unescaped "["
// can't appear in a label, which keeps parsing linear and stops an unclosed
// "@[" typed earlier on the line from swallowing the next mention.
const NOTE_MENTION = /@\[((?:[^[\]\\\n]|\\.)*)\]\(relay:\/\/([a-z]+)\/([0-9a-fA-F-]{36})\)/g;

/** A mention id is a record UUID. */
export function isMentionId(id: string): boolean {
  return UUID.test(id);
}

export type NoteSegment = { text: string } | { mention: MentionTarget & { label: string } };

export function serializeNoteMention({ type, id, label }: MentionTarget & { label: string }): string {
  return `@[${label.replace(/[\\[\]]/g, "\\$&").replace(/\s*\n\s*/g, " ")}](relay://${type}/${id})`;
}

/** Text and mentions in order. A malformed token (unknown type, bad id) stays text. */
export function parseNoteBody(body: string): NoteSegment[] {
  const segments: NoteSegment[] = [];
  let last = 0;
  for (const match of body.matchAll(NOTE_MENTION)) {
    const [token, rawLabel, type, rawId] = match;
    const id = rawId.toLowerCase();
    if (!isWorkspaceObjectType(type) || !UUID.test(id)) continue;
    if (match.index > last) segments.push({ text: body.slice(last, match.index) });
    segments.push({ mention: { type, id, label: rawLabel.replace(/\\(.)/g, "$1") } });
    last = match.index + token.length;
  }
  if (last < body.length) segments.push({ text: body.slice(last) });
  return segments;
}

/** Distinct note mention targets in order (the note equivalent of extractHtmlMentions). */
export function extractNoteMentions(body: string): MentionTarget[] {
  const targets = new Map<string, MentionTarget>();
  for (const segment of parseNoteBody(body)) {
    if (!("mention" in segment)) continue;
    const { type, id, label } = segment.mention;
    if (!targets.has(`${type}:${id}`)) targets.set(`${type}:${id}`, { type, id, label });
  }
  return [...targets.values()];
}

/** The note as people read it: every mention as "@Label". For AI extraction, search and previews. */
export function noteBodyToPlain(body: string): string {
  return parseNoteBody(body).map((s) => ("mention" in s ? `@${s.mention.label}` : s.text)).join("");
}
